/**
 * One critic's answer, wherever it was asked.
 *
 * The same card serves the cellar panel on `Ask`, the shelf panel on `Results`
 * and the restaurant table on `Ask` in menu mode, because a `PanelAnswer` is a
 * `PanelAnswer` — what the critics were standing in front of is said by the
 * heading above them rather than by anything in here.
 *
 * Two things are conditional, and both are absent rather than empty when they
 * do not apply. A menu pick has no `wineId` to link to, because a line on a
 * list was never researched and is not in the catalogue. And only a menu pick
 * carries a `markup`, which is the one fact on this card that the diner could
 * not have worked out for themselves.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { useVoicePlayback } from '../hooks/useVoicePlayback'
import { api } from '../lib/api'
import type { PanelAnswer } from '../lib/types'

export default function Voice({ answer }: { answer: PanelAnswer }) {
  const voicesQuery = useQuery({ queryKey: ['voices'], queryFn: api.voices })
  const hasVoice = (voicesQuery.data?.voices ?? []).some((v) => v.id === answer.personaId)
  const speechText = [answer.headline, answer.answer].filter(Boolean).join(' ')
  const { isLoading, isPlaying, toggle } = useVoicePlayback(
    speechText,
    hasVoice ? answer.personaId : undefined,
  )

  if (answer.error) {
    return (
      <article className="card space-y-1 p-4">
        <span className="pill border border-white/10 bg-white/5 text-stone-400">
          {answer.personaName}
        </span>
        <p className="text-xs text-stone-500">
          Could not be reached, and did not answer: {answer.error}
        </p>
      </article>
    )
  }

  return (
    <article className="card space-y-2 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="pill border border-wine-500/40 bg-wine-800/40 text-wine-100">
          {answer.personaName}
        </span>
        <span className="pill bg-white/5 text-stone-400">{answer.lensName}</span>
        {hasVoice && (
          <button
            onClick={() => void toggle()}
            disabled={isLoading}
            className="pill ml-auto flex items-center gap-1 bg-white/5 text-stone-400 hover:bg-wine-600/30 hover:text-wine-100"
            title={`Listen to ${answer.personaName} with ElevenLabs voice`}
          >
            {isLoading ? (
              <span className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-2 border-stone-400 border-t-transparent" />
            ) : isPlaying ? (
              '⏸'
            ) : (
              '🔊'
            )}
          </button>
        )}
      </div>

      {answer.headline && (
        <p className="font-display text-base leading-snug text-stone-100">
          {answer.headline}
        </p>
      )}

      <p className="text-xs leading-relaxed whitespace-pre-wrap text-stone-300">
        {answer.answer}
      </p>

      {answer.markup && (
        <div className="rounded-lg border border-white/10 bg-black/20 p-2.5 text-[11px] leading-relaxed">
          <div className="flex flex-wrap items-center gap-x-2">
            <span className="font-medium text-stone-200">
              {answer.markup.markup ?? 'Not priced'}
            </span>
            {answer.markup.retailEstimate != null && (
              <span className="text-stone-500">
                about {answer.markup.currency} {answer.markup.retailEstimate} in a
                shop
              </span>
            )}
          </div>
          <p className="pt-0.5 text-stone-400">{answer.markup.verdict}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-[11px]">
        {answer.wineId ? (
          <Link to={`/wine/${answer.wineId}`} className="text-wine-300 hover:underline">
            {answer.displayName ?? 'the bottle'} →
          </Link>
        ) : answer.displayName ? (
          // A menu pick: real, named, and deliberately not a link. There is no
          // wine page for a line on a restaurant list.
          <span className="text-stone-300">{answer.displayName}</span>
        ) : (
          // `_resolve` in panel.py hands back nothing when a critic picks a
          // number that is not on the list. The prose is still worth reading.
          <span className="text-stone-600">no bottle picked</span>
        )}
        {answer.runnerUpName && (
          <span className="text-stone-600">
            otherwise{' '}
            {answer.runnerUpWineId ? (
              <Link
                to={`/wine/${answer.runnerUpWineId}`}
                className="text-stone-400 hover:underline"
              >
                {answer.runnerUpName}
              </Link>
            ) : (
              answer.runnerUpName
            )}
          </span>
        )}
      </div>
    </article>
  )
}
